import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { BOMStatus, ItemStatus, TreeNode } from '@/types/bom';
import { useToast } from '@/hooks/useToast';

interface UseBOMDetailsProps {
  bomId: string;
}

interface UseBOMDetailsReturn {
  bom: any | null;
  isLoading: boolean;
  hierarchy: TreeNode[];
  selectedComponentId: string | null;
  setSelectedComponentId: (id: string | null) => void;
  updateComponentStatus: (id: string, status: ItemStatus) => Promise<void>;
  updateBOMStatus: (status: BOMStatus) => Promise<void>;
  addComponent: (data: any) => Promise<{ id: string }>;
  moveComponent: (id: string, newParentId: string | null) => Promise<void>;
  resequenceComponent: (id: string, newSequence: number) => Promise<void>;
  deleteComponent: (id: string) => Promise<void>;
  createNewVersion: (data: { changes: string; reason: string }) => Promise<void>;
  exportBOM: () => void;
  shareBOM: () => void;
  refreshData: () => Promise<void>;
}

export const useBOMDetails = ({ bomId }: UseBOMDetailsProps): UseBOMDetailsReturn => {
  const [bom, setBom] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hierarchy, setHierarchy] = useState<TreeNode[]>([]);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch BOM data
  const fetchBomData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch BOM main data
      const { data: bomData, error: bomError } = await supabase
        .from('boms')
        .select(`
          *,
          project:project_id(*),
          design:design_id(*)
        `)
        .eq('id', bomId)
        .single();

      if (bomError) throw bomError;

      if (!bomData) {
        toast({
          title: 'BOM not found',
          description: 'The requested BOM could not be found',
          variant: 'destructive',
        });
        return;
      }

      setBom(bomData);

      // Fetch BOM components/hierarchy
      const { data: hierarchyData, error: hierarchyError } = await supabase
        .from('bom_components')
        .select('*')
        .eq('bom_id', bomId)
        .order('sequence_order', { ascending: true });

      if (hierarchyError) throw hierarchyError;

      // Transform flat list into hierarchical tree
      const transformedHierarchy = transformToHierarchy(hierarchyData || []);
      setHierarchy(transformedHierarchy);

      // If there are components, select the first one
      if (hierarchyData && hierarchyData.length > 0 && !selectedComponentId) {
        setSelectedComponentId(hierarchyData[0].id);
      }
    } catch (error) {
      console.error('Error fetching BOM data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load BOM data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (bomId) {
      fetchBomData();
    }
  }, [bomId]);

  // Transform flat components list into hierarchical tree
  const transformToHierarchy = (components: any[]): TreeNode[] => {
    const map = new Map();
    const roots: TreeNode[] = [];

    // Create a map of all items
    components.forEach(comp => {
      const item = {
        id: comp.id,
        name: comp.component_name || 'Unnamed Component',
        component_name: comp.component_name,
        component_type: comp.component_type,
        parent_id: comp.parent_component_id,
        quantity: comp.quantity || 1,
        unit: comp.unit || 'Nos',
        status: comp.status || 'pending',
        children: [],
        ...comp
      };
      
      map.set(comp.id, item);
    });

    // Build the hierarchy
    components.forEach(comp => {
      const item = map.get(comp.id);
      if (comp.parent_component_id && map.has(comp.parent_component_id)) {
        const parent = map.get(comp.parent_component_id);
        parent.children.push(item);
      } else {
        roots.push(item);
      }
    });

    return roots;
  };

  // Update component status
  const updateComponentStatus = async (id: string, status: ItemStatus) => {
    try {
      const { error } = await supabase
        .from('bom_components')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      const updatedHierarchy = updateComponentStatusInHierarchy(hierarchy, id, status);
      setHierarchy(updatedHierarchy);

      toast({
        title: 'Status Updated',
        description: 'Component status has been updated successfully',
      });
    } catch (error) {
      console.error('Error updating component status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update component status',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Helper to update component status in hierarchy
  const updateComponentStatusInHierarchy = (
    nodes: TreeNode[],
    id: string,
    status: ItemStatus
  ): TreeNode[] => {
    return nodes.map(node => {
      if (node.id === id) {
        return { ...node, status };
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: updateComponentStatusInHierarchy(node.children, id, status),
        };
      }
      return node;
    });
  };

  // Update BOM status
  const updateBOMStatus = async (status: BOMStatus) => {
    try {
      const { error } = await supabase
        .from('boms')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', bomId);

      if (error) throw error;

      // Update local state
      setBom((prev: any) => ({ ...prev, status }));

      toast({
        title: 'Status Updated',
        description: 'BOM status has been updated successfully',
      });
    } catch (error) {
      console.error('Error updating BOM status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update BOM status',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Add a new component
  const addComponent = async (data: any) => {
    try {
      const newComponent = {
        ...data,
        bom_id: bomId,
      };
      
      const { data: insertedComponent, error } = await supabase
        .from('bom_components')
        .insert(newComponent)
        .select()
        .single();

      if (error) throw error;

      // Refresh the hierarchy to include the new component
      fetchBomData();

      toast({
        title: 'Component Added',
        description: 'The component has been added successfully',
      });
      
      return { id: insertedComponent.id };
    } catch (error) {
      console.error('Error adding component:', error);
      toast({
        title: 'Error',
        description: 'Failed to add component',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Move a component
  const moveComponent = async (id: string, newParentId: string | null) => {
    try {
      const { error } = await supabase
        .from('bom_components')
        .update({ parent_component_id: newParentId })
        .eq('id', id);

      if (error) throw error;

      // Refresh data to get the updated hierarchy
      fetchBomData();

      toast({
        title: 'Component Moved',
        description: 'The component has been moved successfully',
      });
    } catch (error) {
      console.error('Error moving component:', error);
      toast({
        title: 'Error',
        description: 'Failed to move component',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Resequence a component
  const resequenceComponent = async (id: string, newSequence: number) => {
    try {
      const { error } = await supabase
        .from('bom_components')
        .update({ sequence_order: newSequence })
        .eq('id', id);

      if (error) throw error;

      // Refresh data to get the updated sequence
      fetchBomData();

      toast({
        title: 'Component Resequenced',
        description: 'The component has been resequenced successfully',
      });
    } catch (error) {
      console.error('Error resequencing component:', error);
      toast({
        title: 'Error',
        description: 'Failed to resequence component',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Delete a component
  const deleteComponent = async (id: string) => {
    try {
      const { error } = await supabase
        .from('bom_components')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh data to update the hierarchy
      fetchBomData();

      // If the selected component was deleted, clear the selection
      if (selectedComponentId === id) {
        setSelectedComponentId(null);
      }

      toast({
        title: 'Component Deleted',
        description: 'The component has been deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting component:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete component',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Export BOM data
  const exportBOM = () => {
    toast({
      title: 'Exporting BOM',
      description: 'BOM data is being exported to Excel format',
    });
    // Implementation would depend on the export format and mechanism
    console.log('Export BOM:', bomId);
  };

  // Share BOM
  const shareBOM = () => {
    const shareUrl = `${window.location.origin}/design/bom/${bomId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast({
        title: 'Share Link Copied',
        description: 'BOM share link has been copied to clipboard',
      });
    });
  };

  // Refresh data
  const refreshData = async () => {
    await fetchBomData();
  };

  // Create a new BOM version
  const createNewVersion = async (data: { changes: string; reason: string }) => {
    try {
      // Get current version
      const currentVersion = bom?.version || 1;
      const newVersion = currentVersion + 1;
      
      // Update BOM with new version
      const { error: bomUpdateError } = await supabase
        .from('boms')
        .update({ 
          version: newVersion,
          updated_at: new Date().toISOString() 
        })
        .eq('id', bomId);

      if (bomUpdateError) throw bomUpdateError;
      
      // Record version history
      const { error: versionError } = await supabase
        .from('bom_versions')
        .insert({
          bom_id: bomId,
          version_number: newVersion,
          changes: data.changes,
          reason: data.reason,
          created_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (versionError) throw versionError;
      
      // Update local state
      setBom((prev: any) => ({
        ...prev,
        version: newVersion
      }));
      
      toast({
        title: 'Version Created',
        description: `BOM version ${newVersion} has been created successfully`,
      });
    } catch (error) {
      console.error('Error creating new version:', error);
      toast({
        title: 'Error',
        description: 'Failed to create new version',
        variant: 'destructive',
      });
      throw error;
    }
  };

  return {
    bom,
    isLoading,
    hierarchy,
    selectedComponentId,
    setSelectedComponentId,
    updateComponentStatus,
    updateBOMStatus,
    addComponent,
    moveComponent,
    resequenceComponent,
    deleteComponent,
    createNewVersion,
    exportBOM,
    shareBOM,
    refreshData
  };
};
